import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import { FaArrowLeft, FaPaperPlane } from 'react-icons/fa';

const Container = styled.div`
  padding: 1rem;
  height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 16px;
  cursor: pointer;
  padding: 0.5rem 0;
  margin-bottom: 1rem;

  svg {
    font-size: 20px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  color: var(--text-primary);
  margin: 0 0 1.5rem 0;
`;

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: var(--bg-secondary);
  border-radius: 10px;
  overflow: hidden;
`;

const MessagesContainer = styled.div`
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Message = styled.div<{ isUser: boolean }>`
  max-width: 70%;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  background-color: ${props => props.isUser ? 'var(--primary-color)' : 'var(--bg-primary)'};
  color: ${props => props.isUser ? 'white' : 'var(--text-primary)'};
  align-self: ${props => props.isUser ? 'flex-end' : 'flex-start'};
`;

const InputContainer = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background-color: var(--bg-primary);
  border-top: 1px solid var(--border-color);
`;

const Input = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 5px;
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const SendButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--primary-color);
  color: white;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--primary-color-dark);
  }

  &:disabled {
    background-color: var(--bg-secondary);
    color: var(--text-secondary);
    cursor: not-allowed;
  }

  svg {
    font-size: 18px;
  }
`;

const Chat: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: t('help.chat.welcome'),
      isUser: false
    }
  ]);

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    const newMessage = {
      id: messages.length + 1,
      text: message,
      isUser: true
    };

    setMessages([...messages, newMessage]);
    setMessage('');

    // Simuler une réponse automatique
    setTimeout(() => {
      const response = {
        id: messages.length + 2,
        text: t('help.chat.autoResponse'),
        isUser: false
      };
      setMessages(prev => [...prev, response]);
    }, 1000);
  };

  return (
    <Container>
      <BackButton onClick={() => navigate('/profile/help')}>
        <FaArrowLeft />
        {t('common.back')}
      </BackButton>

      <Title>{t('help.chat.title')}</Title>

      <ChatContainer>
        <MessagesContainer>
          {messages.map(msg => (
            <Message key={msg.id} isUser={msg.isUser}>
              {msg.text}
            </Message>
          ))}
        </MessagesContainer>

        <form onSubmit={handleSend}>
          <InputContainer>
            <Input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('help.chat.placeholder')}
            />
            <SendButton type="submit" disabled={!message.trim()}>
              <FaPaperPlane />
            </SendButton>
          </InputContainer>
        </form>
      </ChatContainer>
    </Container>
  );
};

export default Chat; 